import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * PartnersApiService (Angular 18) — partner/reseller accounts, account
 * categories, partner contacts, payments, notes, templates, and reseller event
 * lookups.
 *
 * Replaces apps/b2b-ui/common/services/api/partnersApi/partnersApi.service.js.
 *
 * Eight sub-namespaces:
 *   apiService.partners.categories.{list,detail,remove,create,update,
 *                                   assignment,assign,unassign,reassign}
 *   apiService.partners.notes.update
 *   apiService.partners.templates.{detail,update}
 *   apiService.partners.events.{detail,create,update,remove}
 *   apiService.partners.reseller.{event,events}
 *   apiService.partners.payments.{account,detail,update,create,remove}
 *   apiService.partners.user.{update,create}
 *   apiService.partners.contacts.{list,create,update,remove}
 *
 * Legacy quirks preserved:
 *   - Top-level `list` / `detail` and all `categories.*` URLs use the v2 path
 *     (legacy `this.service.replace(/v1/, 'v2')`).
 *   - `approve`, `decline`, top-level `update`, and `create` still POST/PUT
 *     against the v1 path (legacy left them un-swapped).
 *   - `create` POSTs to `/v1/api/partners/approve/` (trailing slash, no id) —
 *     the legacy endpoint shape, NOT a typo.
 *   - `categories.assignment(id)` and `categories.assign({...})` both hit
 *     `/v2/api/account/categories/relations` (the `id` arg on `assignment` is
 *     ignored by the legacy too — preserved).
 *   - `contacts.list` swaps to v2 (`/v2/api/partners/account/:id/user`);
 *     create/update/remove stay on v1 (`/v1/api/partners/account/user[/:id]`).
 *   - `templates.update` uses POST (not PUT) — explicit legacy decision because
 *     "all operations are post, put, or delete we are opting to use POST".
 *   - `reseller.events` builds its own querystring inline (only forwards
 *     `startTime`, `endTime`, `templateId`, `type`); preserved 1:1.
 */
const PARTNERS_BASE_V1 = '/v1/api/partners';
const PARTNERS_BASE_V2 = '/v2/api/partners';
const PARTNERS_USER_BASE_V2 = '/v2/api/partners/account/user';
const PARTNERS_CONTACT_BASE_V1 = '/v1/api/partners/account';
const PARTNERS_CONTACT_BASE_V2 = '/v2/api/partners/account';
const PARTNERS_NOTES_BASE_V1 = '/v1/api/partners/notes';
const CATEGORIES_BASE_V2 = '/v2/api/account/categories';
const EVENT_SCHEDULE_BASE_V1 = '/v1/api/accountcategoryeventschedule';
const RESELLER_BASE_V1 = '/v1/api/reseller';
const TEMPLATES_BASE_V1 = '/v1/api/accountcategoryeventschedule/relations/eventTemplates';
const PAYMENTS_BASE_V1 = '/v1/api/policy/payments';

const PARTNERS_LIST_FIELDS = [
  'status',
  ...PAGINATION_SEARCH_FIELDS,
  'type',
  'category',
] as const;

const CATEGORIES_LIST_FIELDS = ['status', ...PAGINATION_SEARCH_FIELDS] as const;

const CONTACTS_LIST_FIELDS = PAGINATION_SEARCH_FIELDS;

interface CompanyProps {
  id?: number | string;
  name?: string;
  companyUrl?: string;
  phoneNumber?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  timezone?: string;
}

interface CreatePartnerProps {
  category: { id: number | string };
  primaryContact: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
  };
  company: CompanyProps & { name: string };
}

interface CategoryPayload {
  id?: number | string;
  name: string;
  description?: string;
}

interface CategoryAssignProps {
  accountId: number | string;
  categoryId: number | string;
}

interface CategoryReassignProps extends CategoryAssignProps {
  id: number | string;
}

interface NotesUpdateProps {
  id: number | string;
  notes: string;
}

interface TemplatesUpdateProps {
  eventTemplateId: number | string;
  data: unknown;
}

interface EventRelationProps {
  id?: number | string;
  discountId?: number | string;
  categoryId?: number | string;
  eventTemplateId?: number | string;
}

interface ResellerEventProps {
  accountId: number | string;
  eventId: number | string;
}

interface ResellerEventsProps {
  accountId: number | string;
  startTime?: string | number;
  endTime?: string | number;
  templateId?: number | string;
  type?: string;
}

interface PaymentPolicyProps {
  id?: number | string;
  type?: string;
  accountId?: number | string;
  uniqueVoucherNumbers?: unknown;
  ticketDelivery?: unknown;
  taxExempt?: unknown;
  balanceDueDays?: number;
  depositDueDays?: number;
  depositMinimumAmount?: number;
  depositMinimumPercent?: number;
}

interface PartnerUserProps {
  id?: number | string;
  accountId: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  primary?: boolean;
  phoneNumber?: string;
}

interface ContactProps {
  id?: number | string;
  accountId: number | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  primary?: boolean;
}

function buildPaymentPolicyBody(props: PaymentPolicyProps, includeId: boolean) {
  return {
    ...(includeId ? { id: props.id } : {}),
    type: props.type,
    accountId: props.accountId,
    uniqueVoucherNumbers: props.uniqueVoucherNumbers,
    ticketDelivery: props.ticketDelivery,
    taxExempt: props.taxExempt,
    balanceDue: {
      days: props.balanceDueDays,
    },
    deposit: {
      daysBeforeEvent: props.depositDueDays,
      minAmount: props.depositMinimumAmount,
      percent: props.depositMinimumPercent,
    },
  };
}

function buildContactBody(props: ContactProps, includeId: boolean) {
  return {
    ...(includeId ? { id: props.id } : {}),
    accountId: props.accountId,
    firstName: props.firstName,
    lastName: props.lastName,
    email: props.email,
    phoneNumber: props.phoneNumber,
    streetAddress1: props.streetAddress1,
    streetAddress2: props.streetAddress2,
    city: props.city,
    state: props.state,
    zipCode: props.zipCode,
    country: props.country,
    primary: includeId ? props.primary || false : false,
  };
}

@Injectable({ providedIn: 'root' })
export class PartnersApiService {
  private readonly http = inject(HttpClient);

  // ─── Top-level partners ────────────────────────────────────────────

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(PARTNERS_BASE_V2, {
        params: buildParams(props, PARTNERS_LIST_FIELDS),
      }),
    );
  }

  detail(partnerId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${PARTNERS_BASE_V2}/${partnerId}`));
  }

  approve(requestId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${PARTNERS_BASE_V1}/approve/${requestId}`, null));
  }

  decline(requestId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${PARTNERS_BASE_V1}/decline/${requestId}`, null));
  }

  update(props: { company: CompanyProps }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(PARTNERS_BASE_V1, {
        id: props.company.id,
        name: props.company.name,
        companyUrl: props.company.companyUrl,
        phoneNumber: props.company.phoneNumber,
        streetAddress1: props.company.streetAddress1,
        streetAddress2: props.company.streetAddress2,
        city: props.company.city,
        state: props.company.state,
        zipCode: props.company.zipCode,
        country: props.company.country,
        timezone: props.company.timezone,
      }),
    );
  }

  create(props: CreatePartnerProps): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${PARTNERS_BASE_V1}/approve/`, {
        categoryId: props.category.id,
        partner: {
          contactFirstName: props.primaryContact.firstName,
          contactLastName: props.primaryContact.lastName,
          contactEmail: props.primaryContact.email,
          contactPhone: props.primaryContact.phoneNumber,
          companyName: props.company.name,
          companyUrl: props.company.companyUrl,
          companyPhone: props.company.phoneNumber,
          billingAddress1: props.company.streetAddress1,
          billingAddress2: props.company.streetAddress2,
          billingCity: props.company.city,
          billingState: props.company.state,
          billingZip: props.company.zipCode,
          country: props.company.country,
          timezone: props.company.timezone,
        },
      }),
    );
  }

  // ─── Categories ────────────────────────────────────────────────────

  readonly categories = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(CATEGORIES_BASE_V2, {
          params: buildParams(props, CATEGORIES_LIST_FIELDS),
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${CATEGORIES_BASE_V2}/relations`, {
          params: buildParams({ categoryId: id }, ['categoryId']),
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${CATEGORIES_BASE_V2}/${id}`)),

    create: (props: CategoryPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(CATEGORIES_BASE_V2, {
          name: props.name,
          description: props.description,
        }),
      ),

    update: (props: CategoryPayload & { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${CATEGORIES_BASE_V2}/${props.id}`, {
          id: props.id,
          name: props.name,
          description: props.description,
        }),
      ),

    // Legacy ignores the `id` argument — the URL never interpolates it. Kept
    // for signature parity with the legacy API surface.
    assignment: (_id?: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${CATEGORIES_BASE_V2}/relations`)),

    assign: (props: CategoryAssignProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${CATEGORIES_BASE_V2}/relations`, {
          accountId: props.accountId,
          categoryId: props.categoryId,
        }),
      ),

    unassign: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${CATEGORIES_BASE_V2}/relations/${id}`)),

    reassign: (props: CategoryReassignProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${CATEGORIES_BASE_V2}/relations/${props.id}`, {
          id: props.id,
          accountId: props.accountId,
          categoryId: props.categoryId,
        }),
      ),
  };

  // ─── Notes ─────────────────────────────────────────────────────────

  readonly notes = {
    update: (props: NotesUpdateProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${PARTNERS_NOTES_BASE_V1}/${props.id}`, {
          notes: props.notes,
        }),
      ),
  };

  // ─── Templates ─────────────────────────────────────────────────────

  readonly templates = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${TEMPLATES_BASE_V1}/${id}`)),

    // POST instead of PUT — see file header note.
    update: (props: TemplatesUpdateProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${TEMPLATES_BASE_V1}/${props.eventTemplateId}`, props.data)),
  };

  // ─── Events (account category event schedule relations) ────────────

  readonly events = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${EVENT_SCHEDULE_BASE_V1}/relations/${id}`)),

    create: (props: EventRelationProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${EVENT_SCHEDULE_BASE_V1}/relations`, {
          discountId: props.discountId,
          categoryId: props.categoryId,
          eventTemplateId: props.eventTemplateId,
        }),
      ),

    update: (props: EventRelationProps & { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(`${EVENT_SCHEDULE_BASE_V1}/relations/${props.id}`, {
          id: props.id,
          discountId: props.discountId,
          categoryId: props.categoryId,
          eventTemplateId: props.eventTemplateId,
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${EVENT_SCHEDULE_BASE_V1}/relations/${id}`)),
  };

  // ─── Reseller ──────────────────────────────────────────────────────

  readonly reseller = {
    event: (props: ResellerEventProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${RESELLER_BASE_V1}/event/instances/${props.accountId}/${props.eventId}`),
      ),

    events: (props: ResellerEventsProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${RESELLER_BASE_V1}/event/instances/${props.accountId}`, {
          params: buildParams(
            {
              startTime: props.startTime,
              endTime: props.endTime,
              templateId: props.templateId,
              type: props.type,
            },
            ['startTime', 'endTime', 'templateId', 'type'],
          ),
        }),
      ),
  };

  // ─── Payments ──────────────────────────────────────────────────────

  readonly payments = {
    account: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${PAYMENTS_BASE_V1}/account/${id}`)),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${PAYMENTS_BASE_V1}/${id}`)),

    update: (props: PaymentPolicyProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(PAYMENTS_BASE_V1, buildPaymentPolicyBody(props, true))),

    create: (props: PaymentPolicyProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(PAYMENTS_BASE_V1, buildPaymentPolicyBody(props, false))),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${PAYMENTS_BASE_V1}/${id}`)),
  };

  // ─── User (partner account contact user) ───────────────────────────

  readonly user = {
    update: (props: PartnerUserProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(PARTNERS_USER_BASE_V2, {
          id: props.id,
          accountId: props.accountId,
          firstName: props.firstName,
          lastName: props.lastName,
          email: props.email,
          primary: props.primary,
          phoneNumber: props.phoneNumber,
        }),
      ),

    create: (props: PartnerUserProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(PARTNERS_USER_BASE_V2, {
          accountId: props.accountId,
          firstName: props.firstName,
          lastName: props.lastName,
          email: props.email,
          phoneNumber: props.phoneNumber,
        }),
      ),
  };

  // ─── Contacts (partner account contacts) ───────────────────────────

  readonly contacts = {
    list: (id: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${PARTNERS_CONTACT_BASE_V2}/${id}/user`, {
          params: buildParams(props, CONTACTS_LIST_FIELDS),
        }),
      ),

    create: (props: ContactProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(`${PARTNERS_CONTACT_BASE_V1}/user`, buildContactBody(props, false))),

    update: (props: ContactProps & { id: number | string; primary?: boolean }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${PARTNERS_CONTACT_BASE_V1}/user`, buildContactBody(props, true))),

    remove: (id: number | string, props: ContactProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.delete<unknown>(`${PARTNERS_CONTACT_BASE_V1}/user/${id}`, {
          body: buildContactBody({ ...props, id }, true),
        }),
      ),
  };
}
